import React, { createContext, useState, useContext, useEffect } from "react";
import { ToastContainer } from 'react-toastify';
import axios from "axios";

import ActionShow from "./ActionShow";
import { parseLiquid } from "../../BeefreeTemplates/ErrorsContainer";
import { toastSuccess, toastError } from "../../../../shared/toastHelper";
import { useTemplateAutosave } from "../../../../shared/useTemplateAutosave";

const ActionContext = createContext();

export function useActionContext() {
  return useContext(ActionContext);
}

const ActionProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    venueOwnership,
    campaign,
    action,
    beefreeToken,
    sampleEventIds,
    sendDomain,
  } = props;

  const isEmailTemplate = action.type === "CampaignActions::Email";
  const beefreeTemplateId = action.beefree_template?.id;

  const [sampleFanId, setSampleFanId] = useState(props.sampleFanId);
  const [tab, setTab] = useState("content");
  const [body, setBody] = useState(action.body);
  const [campaignState, setCampaignState] = useState(campaign.state);
  const [parsedBody, setParsedBody] = useState("");
  const [beefreeTemplate, setBeefreeTemplate] = useState(action.beefree_template);
  const [template, setTemplate] = useState(action.beefree_template?.template);
  const [processedTemplate, setProcessedTemplate] = useState(template);
  const [errors, setErrors] = useState([]);
  const [selectedEventIndex, setSelectedEventIndex] = useState(1);
  const [sampleEventId, setSampleEventId] = useState(sampleEventIds[selectedEventIndex - 1]);
  const [sampleData, setSampleData] = useState({event: {}, fan: {}});
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [subject, setSubject] = useState(action.subject);
  const [preheader, setPreheader] = useState(action.preheader);

  const updateAction = (requestBody) => {
    // Kill any queued/in-flight autosave first so it can't land after this
    // manual write and revert it.
    cancelAutosave();
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/campaigns/${campaign.id}/campaign_actions/${action.id}`, requestBody)
      .then(() => toastSuccess("Automation updated successfully"))
      .catch(() => toastError("Could not save automation, please try again"));
  };

  // Silent autosave, same payload as the manual Save but WITHOUT updateAction's
  // success toast (which would spam on every debounced save) and without feeding
  // the response back into state. Guarded on beefree_template.id (present for
  // email actions, which is the only type that renders the editor).
  const { isAutosaving, autosave: autosaveTemplate, cancel: cancelAutosave } = useTemplateAutosave((templateData, cancelToken) => {
    if (!action.beefree_template?.id) return Promise.resolve();

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.put(`/teams/${team.id}/campaigns/${campaign.id}/campaign_actions/${action.id}`, {
      campaign_action: {
        beefree_template_attributes: {
          id: action.beefree_template.id,
          template: templateData,
        },
      },
    }, { cancelToken });
  });

  useEffect(() => {
    if (!!sampleFanId && !!sampleEventId) {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/campaigns/${campaign.id}/sample_event_data`, {
        params: {
          fan_id: sampleFanId,
          event_id: sampleEventId
        }
      })
        .then(({ data }) => {
          setSampleData(data);
        })
        .catch((_error) => {
          toastError("Could not fetch sample data, please try again");
        });
    }
  }, [sampleFanId, sampleEventId]);

  useEffect(() => {
    setSampleEventId(sampleEventIds[selectedEventIndex - 1])
  }, [selectedEventIndex]);

  const value = {
    csrfToken,
    user,
    team,
    venueOwnership,
    campaign,
    action,
    beefreeToken,
    isEmailTemplate,
    beefreeTemplateId,
    sampleEventIds,
    sampleFanId,
    setSampleFanId,
    tab,
    setTab,
    body,
    setBody,
    campaignState,
    setCampaignState,
    parsedBody,
    setParsedBody,
    template,
    setTemplate,
    errors,
    setErrors,
    selectedEventIndex,
    setSelectedEventIndex,
    sampleEventId,
    setSampleEventId,
    sampleData,
    setSampleData,
    testModalOpen,
    setTestModalOpen,
    parseLiquid,
    updateAction,
    autosaveTemplate,
    isAutosaving,
    subject,
    setSubject,
    preheader,
    setPreheader,
    sendDomain,
    beefreeTemplate,
    setBeefreeTemplate,
    processedTemplate,
    setProcessedTemplate,
  };

  return (
    <ActionContext.Provider value={value}>
      {props.children}
    </ActionContext.Provider>
  );
};

const ActionContainer = (props) => (
  <ActionProvider {...props}>
    <ToastContainer icon={false} />
    <ActionShow />
  </ActionProvider>
);

export default ActionContainer;
